import React, { useState } from 'react';
import { useGame } from '@/app/providers/GameContextProvider';
import { GameStep } from '@/lib/types/common/GameStep';
import { useTranslation } from '@/hooks/useTranslation';
import { setStoredAvatarId } from '@/lib/utils/avatarUtils';
import { Player } from '@/lib/types/common/Player';
import { Avatar } from '@/lib/types/common/Avatar';
import { Team } from '@/lib/types/common/Team';
import AvatarSelector from './AvatarSelector';
import TeamPanel from './TeamPanel';
import UnassignedPlayersPanel from './UnassignedPlayersPanel';
import { useGlobalPopup } from '@/app/providers/GlobalPopupProvider';
import { AUDIO, useAudio } from '@/app/providers/AudioProvider';

const TeamPlayersList: React.FC = () => {
    const { gameState, room } = useGame();
    const { t } = useTranslation();
    const [showAvatarSelector, setShowAvatarSelector] = useState(false);
    const { showPopup } = useGlobalPopup();
    const { play } = useAudio();

    const handleAvatarSelect = async (avatar: Avatar) => {
        if (!room) return;
        try {
            setStoredAvatarId(avatar.id);
            room.send("updatePlayerAvatar", { avatarId: avatar.id });
            showPopup({ type: 'success', message: t("lobby.avatarUpdated") || "Avatar updated!" });
        } catch {
            showPopup({ type: 'error', message: t("lobby.failedToUpdateAvatar") || "Failed to update avatar" });
        }
    };

    if (!gameState || !room) return null;

    const currentPlayer = gameState.players?.get(room.sessionId);
    const currentTeamId = currentPlayer?.teamId || '';
    const isInLobby = (currentPlayer?.inLobby && gameState.gameStep === GameStep.RECAP) || gameState.gameStep === GameStep.LOBBY;
    const canJoin = gameState.gameStep === GameStep.LOBBY || Boolean(currentPlayer?.inLobby && gameState.gameStep === GameStep.RECAP);

    const handleJoinTeam = (teamId: string) => {
        room.send('joinTeam', { teamId });
    };

    const playersArray = Array.from(gameState.players?.entries() || []);

    const getTeamPlayers = (team: Team) => {
        const teamPlayers: { sessionId: string; player: Player; index: number }[] = [];
        team.memberSessionIds.forEach(sessionId => {
            const player = gameState.players?.get(sessionId);
            if (player) {
                const globalIndex = playersArray.findIndex(([sid]) => sid === sessionId);
                teamPlayers.push({
                    sessionId,
                    player,
                    index: globalIndex
                });
            }
        });
        return teamPlayers;
    };

    const getUnassignedPlayers = () => {
        const assignedSessionIds = new Set<string>();
        gameState.teams.forEach(team => {
            team.memberSessionIds.forEach(sessionId => {
                assignedSessionIds.add(sessionId);
            });
        });

        const unassigned: { sessionId: string; player: Player; index: number }[] = [];
        playersArray.forEach(([sessionId, player], index) => {
            if (!assignedSessionIds.has(sessionId)) {
                unassigned.push({ sessionId, player, index });
            }
        });
        return unassigned;
    };

    const unassignedPlayers = getUnassignedPlayers();

    return (
        <>
            {/* Team panels */}
            {gameState.teams.map((team) => (
                <TeamPanel
                    key={team.id}
                    team={team}
                    players={getTeamPlayers(team)}
                    isPlayerTeam={team.id === currentTeamId}
                    onJoin={() => handleJoinTeam(team.id)}
                    canJoin={canJoin}
                    onAvatarClick={() => (play(AUDIO.CLICK), setShowAvatarSelector(true))}
                    currentPlayerSessionId={room.sessionId}
                    gameStep={gameState.gameStep}
                    gameMode={gameState.gameMode}
                    gameMasterId={gameState.gameMasterId}
                    currentRound={gameState.currentRound}
                    currentHazards={Array.from(gameState.currentHazards || [])}
                    isInLobby={isInLobby}
                />
            ))}

            {/* Unassigned players panel */}
            <UnassignedPlayersPanel
                players={unassignedPlayers}
                onAvatarClick={() => (play(AUDIO.CLICK), setShowAvatarSelector(true))}
                currentPlayerSessionId={room.sessionId}
                gameStep={gameState.gameStep}
                gameMasterId={gameState.gameMasterId}
                currentRound={gameState.currentRound}
                currentHazards={Array.from(gameState.currentHazards || [])}
                isInLobby={isInLobby}
            />

            {/* Avatar Selector Modal */}
            <AvatarSelector
                isOpen={showAvatarSelector}
                currentAvatarId={gameState?.players?.get(room?.sessionId || '')?.avatarId}
                onSelect={handleAvatarSelect}
                onClose={() => setShowAvatarSelector(false)}
            />
        </>
    );
};

export default TeamPlayersList;
